// client/src/pages/gallery/GallerySetup.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiFetch } from '../../api/api';
import { toast } from 'react-toastify';
import { readScope } from '../../state/scopeStorage';

export default function GallerySetup() {
  const { id } = useParams(); // "new" or settingId
  const navigate = useNavigate();
  const [columns, setColumns] = useState(4);
  const [primaryView, setPrimaryView] = useState('auto');
  const orgId = readScope()?.org?.id;
  const [allowDelete, setAllowDelete] = useState(true);
  const [loading, setLoading] = useState(true);

  // core config
  const [name, setName] = useState('');
  const [datasetId, setDatasetId] = useState('');

  // settings
  const [bgColor, setBgColor] = useState('#0b1220');
  const [size, setSize] = useState('medium');
  const [printDelayMs, setPrintDelayMs] = useState(5000);
  const [maxCopies, setMaxCopies] = useState(3);

  // datasets
  const [datasets, setDatasets] = useState([]);
  console.log("ID:", id)

  // ---------------------------
  // Load data
  // ---------------------------
  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        // 1. load datasets (org scoped)
        const ds = await apiFetch('/datasets/list', {
          method: 'POST',
          body: { cnt: 0, lmt: 200 }
        });

        if (!cancelled) {
          setDatasets(ds?.items || []);
        }

        // 2. if editing → load existing config
        if (id && id !== 'new') {
          const s = await apiFetch(`/gallery/settings/item/${id}`);

          if (!cancelled && s) {
            setName(s.name || '');
            setDatasetId(s.datasetId || '');
            setBgColor(s.bg || '#0b1220');
            setSize(s.size || 'medium');
            setPrintDelayMs(s.printDelayMs || 5000);
            setMaxCopies(s.maxCopies || 3);
            setColumns(s.columns || 4);
            setPrimaryView(s.primaryView || 'landscape');
            setAllowDelete(s.allowDelete !== false);
          }
        }

      } catch (err) {
        console.error('[GallerySetup load error]', err);
        toast.error('Failed to load settings');
      }

      if (!cancelled) setLoading(false);
    }

    load();

    return () => { cancelled = true; };

  }, [id]);

  const handleReturn = () => {
    if (!id || id === 'new') return;
    navigate(`/gallery/datasets`);
  };

  const handleView = () => {
    navigate(`/gallery/display/${id}`);   // same behavior
  };


  // ---------------------------
  // Save
  // ---------------------------
  const handleSave = async (e) => {
    e.preventDefault();

    if (!name) return toast.error('Name is required');
    if (!datasetId) return toast.error('Project is required');

    try {
      await apiFetch('/gallery/settings', {
        method: 'POST',
        body: {
          _id: id !== 'new' ? id : undefined,
          name,
          datasetId,
          orgId,
          bg: bgColor,
          size,
          printDelayMs,
          columns,
          primaryView,
          maxCopies,
          allowDelete
        }
      });

      toast.success('Gallery configuration saved');

      // navigate('/gallery'); // go back to list

    } catch (err) {
      console.error(err);
      toast.error('Failed to save configuration');
    }
  };

  if (loading) {
    return <div style={{ padding: 16 }}>Loading…</div>;
  }

  return (
    <div style={{ padding: 16, maxWidth: 600 }}>

    <div
      className="datatable-header-row"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 700 }}>
        {id === 'new' ? 'New Gallery Configuration' : 'Edit Gallery Configuration'}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>

        <button
          className="btn-gray"
          onClick={handleReturn}
        >
          Return
        </button>

        <button
          className="btn-green"
          onClick={handleSave}
        >
          Save
        </button>

        <button
          className="btn-blue"
          onClick={handleView}
        >
          View
        </button>

      </div>
    </div>

      <form onSubmit={handleSave} style={{ display: 'grid', gap: 14 }}>

        {/* NAME */}
        <label style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8 }}>
          <span>Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. VIP Booth, Trade Show, Kiosk 1"
          />
        </label>

        {/* DATASET */}
        <label style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8 }}>
          <span>Project</span>
          <select
            value={datasetId}
            onChange={(e) => setDatasetId(e.target.value)}
          >
            <option value="">Select dataset</option>
            {datasets.map(d => (
              <option key={d._id} value={d._id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>

        {/* BACKGROUND */}
        <label style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8 }}>
          <span>Background Color</span>
          <input
            type="color"
            value={bgColor}
            onChange={(e) => setBgColor(e.target.value)}
          />
        </label>

        {/* allow delete */}
        <label style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8 }}>
          <span>Allow Delete</span>
          <input
            type="checkbox"
            checked={allowDelete}
            onChange={(e) => setAllowDelete(e.target.checked)}
          />
        </label>

        <label style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8 }}>
          <span>Columns</span>
          <input
            type="number"
            min={1}
            max={10}
            value={columns}
            onChange={(e) => setColumns(Number(e.target.value))}
          />
        </label>

        <label style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8 }}>
          <span>Primary View</span>
          <select value={primaryView} onChange={(e) => setPrimaryView(e.target.value)}>
            <option value="auto">Auto</option>
            <option value="landscape">Landscape</option>
            <option value="portrait">Portrait</option>
          </select>
        </label>

        {/* PRINT DELAY */}
        <label style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8 }}>
          <span>Print Delay (seconds)</span>
          <input
            type="number"
            min={1}
            value={printDelayMs / 1000}
            onChange={(e) => setPrintDelayMs(Number(e.target.value) * 1000)}
          />
        </label>

        {/* MAX COPIES */}
        <label style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8 }}>
          <span>Max Copies per Request</span>
          <input
            type="number"
            min={1}
            max={3}
            value={maxCopies}
            onChange={(e) => setMaxCopies(Number(e.target.value))}
          />
        </label>

      </form>
    </div>
  );
}
